import type { GymSettingsDto, GymSettingsInput } from '@bjj/shared';
import { useState } from 'react';
import { useApiMutation, useAdminSettings } from '../api/hooks';
import { QuickNotesCard } from '../components/QuickNotes';
import { Card, ErrorMessage, Field, Loading, PageHeader } from '../components/ui';

type Editable = Omit<GymSettingsInput, never>;
const FIELDS: Array<[keyof Editable, string, string?]> = [
  ['gymName', 'Gym name'],
  ['shortName', 'Short name', 'Used in compact places such as email subjects.'],
  [
    'appDisplayName',
    'App display name',
    'Shown inside the app. The installed app name/icon are set per build (see docs).',
  ],
  ['curriculumTitle', 'Adults curriculum title', 'e.g. Blue Belt Curriculum'],
  ['kidsCurriculumTitle', 'Kids curriculum title', 'Shown to kids, e.g. Kids Curriculum'],
  ['logoUrl', 'Logo URL', 'HTTPS link to a square PNG/SVG logo.'],
  ['supportEmail', 'Support email'],
  ['websiteUrl', 'Website URL'],
  ['privacyPolicyUrl', 'Privacy policy URL', 'Required for app store listings.'],
  ['invitationEmailSubject', 'Invitation email subject'],
  ['invitationEmailSignature', 'Invitation email signature'],
];

export function SettingsPage() {
  const settings = useAdminSettings();
  const [draft, setDraft] = useState<Partial<Editable>>({});
  const [saved, setSaved] = useState(false);
  const save = useApiMutation<void, GymSettingsDto>(
    () => ({ path: '/admin/settings', method: 'PUT', body: draft }),
    [
      ['admin', 'settings'],
      ['settings', 'public'],
    ],
  );
  if (!settings.data) return settings.error ? <ErrorMessage error={settings.error} /> : <Loading />;
  const value = { ...settings.data, ...draft };
  const set = (patch: Partial<Editable>) => {
    setSaved(false);
    setDraft({ ...draft, ...patch });
  };

  return (
    <>
      <PageHeader
        title="Branding & settings"
        subtitle="Changes apply immediately to the app and dashboard — no app update needed."
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(undefined, {
            onSuccess: () => {
              setDraft({});
              setSaved(true);
            },
          });
        }}
      >
        <Card title="Branding">
          {saved ? (
            <div className="alert success" role="status">
              Settings saved.
            </div>
          ) : null}
          <ErrorMessage error={save.error} />
          <div className="form-row">
            {FIELDS.map(([key, label, hint]) => (
              <Field key={key} label={label} hint={hint}>
                {(id) => (
                  <input
                    id={id}
                    type="text"
                    value={String(value[key] ?? '')}
                    onChange={(e) => set({ [key]: e.target.value })}
                  />
                )}
              </Field>
            ))}
          </div>
          <div className="form-row">
            {(['primaryColor', 'secondaryColor'] as const).map((key) => (
              <Field key={key} label={key === 'primaryColor' ? 'Primary color' : 'Secondary color'}>
                {(id) => (
                  <div className="btn-row">
                    <input
                      type="color"
                      aria-label={`${key} picker`}
                      value={value[key]}
                      onChange={(e) => set({ [key]: e.target.value })}
                    />
                    <input
                      id={id}
                      type="text"
                      value={value[key]}
                      onChange={(e) => set({ [key]: e.target.value })}
                      style={{ maxWidth: 120 }}
                    />
                  </div>
                )}
              </Field>
            ))}
          </div>
          <button
            className="btn primary"
            type="submit"
            disabled={save.isPending || Object.keys(draft).length === 0}
          >
            Save settings
          </button>
        </Card>
      </form>
      <QuickNotesCard />
      <Card title="Deployment status">
        <ul>
          <li>
            Email delivery:{' '}
            {settings.data.email.configured
              ? 'configured'
              : 'not configured — invitation links are shown for manual sharing'}
          </li>
        </ul>
      </Card>
    </>
  );
}
